import { useEffect, useState } from 'react';
import type { RefObject } from 'react';

interface HeadingRef {
  el: HTMLElement;
  text: string;
}

export interface ReadingProgress {
  /** True once the reader has scrolled away from the top (drives the floating shadow). */
  scrolled: boolean;
  /** Text of the heading the reader is currently under, or null near the top. */
  activeHeading: string | null;
}

/**
 * Drives the "Living Workbench" header: a single rAF-throttled scroll handler on
 * the document scroller updates a `--mr-progress` (0..1) custom property for the
 * reading-progress line (transform-only, compositor-friendly), a `scrolled`
 * flag, and the active section heading ("you are here"). Heading geometry is
 * read only on animation frames, and React state changes only when the discrete
 * values actually flip, so typing/zooming never thrashes.
 */
export function useReadingProgress(
  scrollRef: RefObject<HTMLElement | null>,
  renderId: number | null,
): ReadingProgress {
  const [scrolled, setScrolled] = useState(false);
  const [activeHeading, setActiveHeading] = useState<string | null>(null);

  useEffect(() => {
    const scroller = scrollRef.current;
    if (!scroller || !renderId) {
      document.documentElement.style.setProperty('--mr-progress', '0');
      setScrolled(false);
      setActiveHeading(null);
      return;
    }

    const headings: HeadingRef[] = Array.from(
      scroller.querySelectorAll<HTMLElement>('.markroom-doc h1, .markroom-doc h2, .markroom-doc h3'),
    ).map((el) => ({ el, text: (el.textContent ?? '').trim() }));

    let frame = 0;
    let lastScrolled: boolean | null = null;
    let lastHeading: string | null = null;

    const update = () => {
      frame = 0;
      const max = scroller.scrollHeight - scroller.clientHeight;
      const progress = max > 0 ? Math.min(1, Math.max(0, scroller.scrollTop / max)) : 0;
      document.documentElement.style.setProperty('--mr-progress', progress.toFixed(4));

      const isScrolled = scroller.scrollTop > 6;
      if (isScrolled !== lastScrolled) {
        lastScrolled = isScrolled;
        setScrolled(isScrolled);
      }

      // Active heading = the last heading whose top has crossed just under the bar.
      const top = scroller.getBoundingClientRect().top;
      let active: string | null = null;
      for (const h of headings) {
        if (h.el.getBoundingClientRect().top - top <= 72) active = h.text;
        else break;
      }
      const resolved = isScrolled ? active : null;
      if (resolved !== lastHeading) {
        lastHeading = resolved;
        setActiveHeading(resolved);
      }
    };

    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };

    update();
    scroller.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      scroller.removeEventListener('scroll', onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [scrollRef, renderId]);

  return { scrolled, activeHeading };
}
